import React, { createContext, useContext, ReactNode } from 'react';
import { useNutritionRealtime } from '@/hooks/nutrition/useNutritionRealtime';
import { useDailySummary, useNutritionGoals } from '@/hooks/nutrition/useNutritionData';
import { NutritionMetrics } from '@/types/nutrition';

interface NutritionContextValue {
  metrics: NutritionMetrics | null;
  isLoading: boolean;
  hasData: boolean;
}

const NutritionContext = createContext<NutritionContextValue>({
  metrics: null,
  isLoading: true,
  hasData: false
});

export const useNutrition = () => useContext(NutritionContext);

interface NutritionProviderProps {
  children: ReactNode;
}

export function NutritionProvider({ children }: NutritionProviderProps) {
  // Set up real-time subscriptions
  useNutritionRealtime({ enabled: true });
  
  // Get core nutrition data
  const { data: dailySummary, isLoading: summaryLoading } = useDailySummary();
  const { data: goals, isLoading: goalsLoading } = useNutritionGoals();

  const isLoading = summaryLoading || goalsLoading;
  const hasData = !!(dailySummary || goals);

  // Calculate metrics
  const metrics: NutritionMetrics | null = React.useMemo(() => {
    if (!dailySummary && !goals) return null;

    const current = dailySummary || {
      total_calories: 0,
      total_protein: 0,
      total_carbs: 0,
      total_fats: 0,
      net_calories: 0
    };

    const goalData = goals || {
      daily_calories: 2000,
      protein_grams: 120,
      carbs_grams: 200,
      fats_grams: 65
    };

    return {
      calories: {
        current: current.total_calories || 0,
        goal: goalData.daily_calories || 2000,
        progress: Math.min(100, ((current.total_calories || 0) / (goalData.daily_calories || 2000)) * 100)
      },
      protein: {
        current: current.total_protein || 0,
        goal: goalData.protein_grams || 120,
        progress: Math.min(100, ((current.total_protein || 0) / (goalData.protein_grams || 120)) * 100)
      },
      carbs: {
        current: current.total_carbs || 0,
        goal: goalData.carbs_grams || 200,
        progress: Math.min(100, ((current.total_carbs || 0) / (goalData.carbs_grams || 200)) * 100)
      },
      fats: {
        current: current.total_fats || 0,
        goal: goalData.fats_grams || 65,
        progress: Math.min(100, ((current.total_fats || 0) / (goalData.fats_grams || 65)) * 100)
      },
      netCalories: {
        value: current.net_calories || 0,
        status: (current.net_calories || 0) > 200 ? 'surplus' : 
                (current.net_calories || 0) < -200 ? 'deficit' : 'maintenance'
      }
    };
  }, [dailySummary, goals]);

  const value: NutritionContextValue = {
    metrics,
    isLoading,
    hasData
  };

  return (
    <NutritionContext.Provider value={value}>
      {children}
    </NutritionContext.Provider>
  );
}